import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { Order } from '../models/order/order.model';
import { BuyersApiService } from './buyers-api.service';
import { OrdersApiService } from './orders-api.service';
import { ShoppingCartService } from './shoppingcart.service';
import { UserApiService } from './user-api.service';

@Injectable({
  providedIn: 'root'
})
export class OrderService implements OnDestroy {

  private subscriptions = new Map<string, Subscription>();

  $order: Order;
  order: BehaviorSubject<Order>;
  $shoppingCartValid = false;
  valid: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public valid$ = this.valid.pipe(shareReplay(1));

  isInternalUser: boolean;

  constructor(
    private buyersApiService: BuyersApiService,
    private shoppingCartService: ShoppingCartService,
    private ordersApiService: OrdersApiService,
    private userApiService: UserApiService,
  ) {
    this.$order = new Order();
    this.order = new BehaviorSubject(this.$order);

    this.subscriptions.set('shoppingCartItems', this.userApiService.isInternalUser$
      .subscribe(isInternalUser => this.isInternalUser = isInternalUser)
    );

    this.subscriptions.set('shoppingCartItems',
      combineLatest([
        this.shoppingCartService.items,
        this.buyersApiService.currencyCode$,
        this.userApiService.isInternalUser$,
        this.shoppingCartService.pricesChanged$,
        this.shoppingCartService.certificatesChanged$,
      ])
        .subscribe(([items, currencyCode, isInternalUser]) => {
          this.$order.packages = items.map(({ id, number, certificateCode, selectedCert, selectedPrice }) =>
            ({ id, number, certificateCode: selectedCert || certificateCode, price: { net: selectedPrice, uom: currencyCode } })
          );
          this.order.next(this.$order);
          this.verifyOrder(isInternalUser);
        })
    );

    this.subscriptions.set('shoppingCartIsValid',
      combineLatest([
        this.shoppingCartService.isValid,
        this.userApiService.isInternalUser$
      ])
        .subscribe(([isValid, isInternalUser]) => {
          this.$shoppingCartValid = isValid;
          this.verifyOrder(isInternalUser);
        })
    );

    // make a new order when sent

    this.subscriptions.set('ordersApiOrderSent',
      this.ordersApiService.orderSent.subscribe(() => this.newOrder())
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  newOrder(): any {
    this.$order = new Order();
    this.order.next(this.$order);
    this.verifyOrder(this.isInternalUser);
  }

  setOrder(order: Order) {
    this.$order = Object.assign({}, this.$order, order);
    this.order.next(this.$order);
    this.verifyOrder(this.isInternalUser);
  }

  updateOrder(patch: any) {
    this.$order = Object.assign({}, this.$order, patch);
    this.order.next(this.$order);
    this.verifyOrder(this.isInternalUser);
  }

  verifyOrder(priceRequired: boolean) {
    this.valid.next(
      (priceRequired ? this.$order?.packages?.every(pkg => pkg.price && pkg.price?.net > 0 && pkg.price.uom) : true) // internal users has to add a price to all packages
      && this.$order?.packages?.every(pkg => !!pkg.certificateCode) // all packages need a certificate code (either preset or selected by user)
      && this.$shoppingCartValid // the rest of the cart must be okay
      || false
    );
  }

  submit() {
    this.ordersApiService.post(this.$order);
  }

}
